/**
 * Diagnóstico de un error de red/API sin datos personales: de dónde viene la
 * falla (conexión, sesión, permiso, validación, NAS o servidor), el estatus, el
 * código del backend (X-Error-Code) y el id de la petición. Lo mismo que usa la
 * app móvil, para que soporte lea igual las fallas de ambos clientes.
 */
const SUPPORT_CODE_LENGTH = 8;
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

function headerValue(headers, name) {
    if (!headers) return undefined;
    const value = typeof headers.get === 'function'
        ? headers.get(name)
        : headers[name.toLowerCase()] ?? headers[name];
    return typeof value === 'string' && value ? value : undefined;
}

function asString(value) {
    return typeof value === 'string' && value ? value : undefined;
}

function sourceOf(status, code) {
    if (status === undefined) return 'red';
    if (code === 'NAS_ERROR') return 'nas';
    if (status === 401) return 'sesion';
    if (status === 403) return 'permiso';
    if (status === 408 || status === 429 || status >= 500) return 'servidor';
    return 'validacion';
}

export function errorMeta(error, now = new Date()) {
    const e = error && typeof error === 'object' ? error : {};
    const response = e.response && typeof e.response === 'object' ? e.response : null;
    const status = typeof response?.status === 'number' && response.status > 0 ? response.status : undefined;
    const code = headerValue(response?.headers, 'x-error-code') || asString(response?.data?.error_code);
    const requestId = headerValue(response?.headers, 'x-request-id') || headerValue(e.config?.headers, 'X-Request-ID');
    let reason;
    if (status === undefined && e.isAxiosError) {
        reason = TIMEOUT_CODES.includes(e.code) ? 'timeout' : 'network';
    }

    return {
        source: sourceOf(status, code),
        ...(status !== undefined ? { status } : {}),
        ...(code ? { code } : {}),
        ...(requestId ? { requestId } : {}),
        ...(reason ? { reason } : {}),
        at: now.toISOString(),
    };
}

/** Los primeros caracteres del id: el usuario los dicta y soporte los busca en los logs. */
export function supportCode(error) {
    const { requestId } = errorMeta(error);
    return requestId ? requestId.slice(0, SUPPORT_CODE_LENGTH) : null;
}

export function withSupportCode(message, error) {
    const code = supportCode(error);
    return code ? `${message} (Código de soporte: ${code})` : message;
}

/**
 * Texto para el usuario cuando falla una subida de archivo. Solo culpa al NAS
 * si el backend lo indicó (X-Error-Code: NAS_ERROR); una caída de red, un
 * archivo muy grande o un permiso ya no se presentan como falla del NAS.
 */
export function uploadErrorMessage(error) {
    const meta = errorMeta(error);
    const data = error?.response?.data;
    const serverText = asString(data?.error) || (meta.status < 500 ? asString(data?.message) : undefined);

    let message;
    if (serverText) message = serverText;
    else if (meta.reason === 'timeout') message = 'La subida tardó demasiado. Revisa tu conexión e intenta de nuevo.';
    else if (meta.source === 'red') message = 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.';
    else if (meta.status === 413) message = 'El archivo es demasiado grande para el servidor.';
    else if (meta.status === 403) message = 'Tu cuenta no tiene permiso para subir archivos aquí.';
    else if (meta.code === 'NAS_ERROR') message = 'El servidor de archivos (NAS) no respondió. Intenta de nuevo en unos minutos.';
    else message = 'No se pudo subir el archivo. Si persiste, contacta a soporte.';

    return withSupportCode(message, error);
}
